import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useToggle from 'hooks/useToggle'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Modal from 'LuxKit/Modal/Modal'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import PaneBody from 'components/Common/Pane/PaneBody'
import HeroImage from 'content/components/HeroImage'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import PageSection from 'content/components/PageSection'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import TextButton from 'LuxKit/Button/TextButton'
import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import Faq from 'components/Common/Faq'
import Competition from './Competition'
import GrandPrizeTile from './GrandPrizeTile'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Error404Page from 'components/Pages/Error404Page'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { COMP_RAFFLE_TYPE } from 'marketing/components/Competitions/constants'
import Group from 'components/utils/Group'
import { useCompetitionConfig, useCompetitionPrize, useCompetitionStatus } from 'marketing/api'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ImageTitle from './ImageTitle'
import CompetitionClosedPage from './CompetitionClosedPage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HeadData from 'components/Common/HeadData'

const StyledHeroImage = styled(HeroImage)`
  padding: ${rem(40)} ${rem(20)};
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
`

const competitionDateFormat = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
} as const

const AlignCenter = styled.div`
  display: flex;
  justify-content: center;
`

const OverLay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
`

const Wrapper = styled.div`
  z-index: ${zIndex.min};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
`

const MinorPrizeTile = styled.div`
  display: flex;
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};

  & > * + * {
    margin-left: ${rem(16)};
  }
`

const StyledPaneBody = styled(PaneBody)`
  padding: ${rem(24)} ${rem(20)} ${rem(20)} ${rem(20)};

  &.minor-prize {
    & > * + * {
      margin-top: ${rem(8)};
    }

    padding-top: 0;
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(24)};

    &.minor-prize {
      padding-top: 0;
    }
  }
`

const StyledBodyText = styled(BodyText)`
  & ul, li {
    margin-top: 0;
  }
`

const StyledClickableLink = styled(ClickableLink)`
  text-decoration: underline;
  display: inline-block;
`

interface Props {
  slug: string
  memberId?: string
  prizeDescription?: string
  showPrizes?: boolean
  regionCode: string
}

function CompetitionPageV2(props: Props) {
  const {
    memberId,
    prizeDescription,
    showPrizes,
    regionCode,
    slug,
  } = props
  let renderPrizeComponent

  const intl = useIntl()

  const { value: isOpenOverlay, off: closeOverLay } = useToggle(true)
  const [isOpenGrandPrizes, , openModalGrandPrizes, closeModalGrandPrizes] = useToggle()
  const [isOpenMinorPrizes, , openModalMinorPrizes, closeModalMinorPrizes] = useToggle()
  const { data: competitionConfig, isLoading, refetch: refetchConfig, error } = useCompetitionConfig(slug, regionCode)
  const { data: prize, refetch: fetchPrize } = useCompetitionPrize(slug, competitionConfig?.episodeId, regionCode)
  const { data: userStatus, isLoading: userStatusLoading, refetch: refetchUserStatus } = useCompetitionStatus(slug, competitionConfig?.episodeId, regionCode)

  const closeOverlayAndReload = () => {
    refetchUserStatus()
    closeOverLay()
  }

  useEffect(() => {
    refetchUserStatus()
    refetchConfig()
  }, [memberId, refetchConfig, refetchUserStatus, regionCode])

  if (isLoading && userStatusLoading) {
    return <LoadingIndicator />
  }

  if (error?.errors?.[0] === 'COMPETITION_ENDED') {
    return <CompetitionClosedPage />
  }

  if (!competitionConfig || (competitionConfig.allowedRegions.length && !competitionConfig.allowedRegions.includes(regionCode.toUpperCase()))) {
    return <Error404Page invalidRegion />
  }

  const {
    endDateUtc,
    grandPrizeDetails,
    payoutConfig,
    drawDateUtc,
    heroImage,
    prizeType,
    prizeContactDateUtc,
    headData,
  } = competitionConfig

  const formattedCloseDate = intl.formatDate(new Date(endDateUtc), competitionDateFormat)
  const formattedDrawDate = intl.formatDate(new Date(drawDateUtc), competitionDateFormat)
  const formattedPrizeContactDate = prizeContactDateUtc
    ? intl.formatDate(new Date(prizeContactDateUtc), competitionDateFormat)
    : null

  const resolvedPrizeDescription = prizeDescription || intl.formatMessage({
    id: '_WBdKOh9LgSdNDCLuWYh',
    defaultMessage: 'Inclusions',
    description: 'Default heading for prize inclusions list in competition page',
  })

  // Markdown language processing
  let description = grandPrizeDetails.offer.description.split(/\r?\n/)[0]
  if (grandPrizeDetails.includes.length > 0) {
    description += `\n\n**${resolvedPrizeDescription}**`
    grandPrizeDetails.includes.forEach((inclusion: string, i: number) => {
      const lineBreak = i === 0 ? '\n\n' : '\n'
      description += `${lineBreak}- ${inclusion}`
    })
  }

  const entriesCloseMessage = intl.formatMessage(
    {
      id: 'Lyud7LeU76BQ9_6furYo',
      defaultMessage: 'Entries close {closeDate}',
      description: 'Competition entries closing date display on competition page',
    },
    { closeDate: formattedCloseDate },
  )
  const codeRedeemMessage = intl.formatMessage({
    id: '-T4Rdp4jY0sAc2SZZu5x',
    defaultMessage: 'Code must be redeemed within 3 days of your spin',
    description: 'Promo code redemption deadline notice on competition page',
  })
  // handle prize type to show message
  const entriesMessage = prizeType === COMP_RAFFLE_TYPE ? entriesCloseMessage : codeRedeemMessage

  const viewPrizeDetailsLabel = intl.formatMessage({
    id: 'jl2dsP-GbUt-dXIAMoII',
    defaultMessage: 'View full prize details',
    description: 'Button text to open grand prize details modal on competition hero',
  })
  const termsApplyLabel = intl.formatMessage({
    id: 'NSRDRj8_GJXC_-BnHjvR',
    defaultMessage: 'Terms and conditions apply',
    description: 'Terms and conditions link text on competition hero',
  })
  const faqHeading = intl.formatMessage({
    id: 'RK87NFc2WyrWpXUeYg9T',
    defaultMessage: 'Frequently asked questions',
    description: 'Section heading for the FAQ section on the competition page',
  })
  const grandPrizeModalTitle = intl.formatMessage({
    id: 'zo6rRzL4_ppH-BnrBDIO',
    defaultMessage: 'Grand prize details',
    description: 'Title of the grand prize details modal on competition page',
  })
  const grandPrizeModalSubtitle = intl.formatMessage(
    {
      id: 'yh0ZTSdkuJ3cu9EhIl5F',
      defaultMessage: 'Entries close {closeDate}',
      description: 'Subtitle of grand prize details modal showing entries close date',
    },
    { closeDate: formattedCloseDate },
  )
  const prizesAvailableTitle = intl.formatMessage({
    id: 'F9Sl0wdTprI-4ja6bS9r',
    defaultMessage: 'Prizes available',
    description: 'Title of the minor prizes modal on competition page',
  })
  const oneEntryPerDayLabel = intl.formatMessage({
    id: 'ztlpkiRTr8HS05LeQZ2C',
    defaultMessage: 'One entry per person per day.',
    description: 'Entry limit notice in minor prizes modal on competition page',
  })
  const toTheDrawLabel = intl.formatMessage({
    id: 'StxkyePW4YU1Ch951K7x',
    defaultMessage: 'to the draw!',
    description: 'Subtitle below prize entry count in minor prizes modal',
  })

  if (userStatus?.alreadyHasReward && !(isLoading || userStatusLoading)) {
    renderPrizeComponent = (
      <PageSection>
        <GrandPrizeTile
          openModalGrandPrizes={openModalGrandPrizes}
          grandPrizeDetails={grandPrizeDetails}
          announcedTime={formattedDrawDate}
          prizeContactDate={formattedPrizeContactDate}
          competitionUserStatus={userStatus}
          promoLink={competitionConfig.promoLink}
        />
      </PageSection>
    )
  }

  return (
    <>
      {headData && <HeadData imageId={headData.thumbnailImage.id} />}
      <VerticalSpacer gap={8}>
        <>
          <StyledHeroImage
            alt={grandPrizeDetails.offer.name}
            aria-hidden="true"
            id={heroImage.id}
            mobileAspectRatio="3:1"
            tabletAspectRatio="3:1"
            desktopAspectRatio="4:1"
            largeDesktopAspectRatio="6:1"
          >
            <OverLay />
            <Wrapper>
              <VerticalSpacer gap={24}>
                <Caption variant="small" colour="dark-contrast" align="center">{competitionConfig.title}</Caption>
                <ImageTitle intro={competitionConfig.heroTextIntro} highlight={competitionConfig.heroTextHighlight} />
                {!userStatus?.alreadyHasReward && showPrizes && (<AlignCenter>
                  <TextButton kind="primary" variant="ghost" size="large" onClick={openModalGrandPrizes}>{viewPrizeDetailsLabel}</TextButton>
                </AlignCenter>)}
                <Group direction="vertical" tabletDirection="horizontal" tabletGap={8} horizontalAlign="center">
                  <BodyText variant="large" align="center" colour="dark-contrast" weight="bold">
                    <StyledClickableLink to="/promotions" target="_blank">{termsApplyLabel}</StyledClickableLink>
                  </BodyText>
                  <BodyText variant="large" align="center" colour="dark-contrast">
                    {entriesMessage}
                  </BodyText>
                </Group>
              </VerticalSpacer>
            </Wrapper>
          </StyledHeroImage>
          {!userStatus?.alreadyHasReward && (<Competition
            openModalMinorPrizes={openModalMinorPrizes}
            drawDate={formattedDrawDate}
            announcedTime={formattedDrawDate}
            promo_link={competitionConfig.promoLink}
            offerId={competitionConfig.grandPrizeDetails.offerId}
            competitionTitle={competitionConfig.title}
            competitionHeader={competitionConfig.header}
            prize={prize}
            memberId={memberId}
            fetchPrize={fetchPrize}
            breadcrumb={competitionConfig.breadcrumb}
            bodyText={competitionConfig.bodyText}
            showPrizes={showPrizes}
            isModalOpen={isOpenOverlay}
            closeModal={closeOverlayAndReload}
            regionCode={regionCode}
            footerText={competitionConfig.footerText}
          />)}
          {renderPrizeComponent}
        </>
      </VerticalSpacer>
      {!!competitionConfig?.faqSlug && <PageSection>
        <VerticalSpacer gap={40}>
          <Heading
            variant="heading3"
          >
            {faqHeading}
          </Heading>
          <Faq maxToShow={10} slug={competitionConfig.faqSlug} />
        </VerticalSpacer>
      </PageSection>}
      <Modal
        isOpen={isOpenGrandPrizes}
        title={grandPrizeModalTitle}
        subtitle={grandPrizeModalSubtitle}
        onClose={closeModalGrandPrizes}
        size="L"
      >
        <ResponsiveImage
          alt={grandPrizeDetails.offer.name}
          id={grandPrizeDetails.offer.image.id}
          mobileAspectRatio="16:8"
          tabletAspectRatio="16:8"
          desktopAspectRatio="16:8"
          largeDesktopAspectRatio="16:8"
        />
        <StyledPaneBody>
          <VerticalSpacer gap={8}>
            <Heading variant="heading3">{grandPrizeDetails.offer.name}</Heading>
            <VerticalSpacer gap={20}>
              <BodyText variant="large">{grandPrizeDetails?.offer?.subtitle}</BodyText>
              <OnlyIfChildren as={StyledBodyText} variant="large">
                <MarkdownRender content={description} />
              </OnlyIfChildren>
            </VerticalSpacer>
          </VerticalSpacer>
        </StyledPaneBody>
      </Modal>
      <Modal
        isOpen={isOpenMinorPrizes}
        title={prizesAvailableTitle}
        onClose={closeModalMinorPrizes}
      >
        <StyledPaneBody className="minor-prize">
          <BodyText variant="large" colour="light-foreground-alt">{oneEntryPerDayLabel}</BodyText>
          {payoutConfig.map((prize: any) => <MinorPrizeTile key={prize.id}>
            <LineGiftIcon size="XL" />
            <div>
              <Heading variant="heading5">
                <FormattedMessage
                  id="ayBraH9FLF5BFzQxhb9f"
                  defaultMessage="{count, plural, one {# entry} other {# entries}}"
                  description="Pluralized number of competition entries displayed as a prize in the minor prizes modal"
                  values={{ count: Number(prize.value) }}
                />
              </Heading>
              <BodyText variant="medium">{toTheDrawLabel}</BodyText>
            </div>
          </MinorPrizeTile>)}
        </StyledPaneBody>
      </Modal>
    </>
  )
}

const mapState = (state: App.State) => {
  return {
    memberId: state.auth.account?.memberId,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapState)(CompetitionPageV2)
